/**
 * „Zeige auch die Bearbeitung genau an“ (User 07.10.2026): was beim Speichern
 * einer Fassung tatsächlich geändert wurde — als ein Satz für die Historie,
 * unabhängig vom Kommentar des Nutzers.
 */
export interface AenderungsAngaben {
    /** Aus dieser früheren Fassung heraus weiterbearbeitet. */
    ausFassung?: number | null;
    /** Ein Bild wird mit dieser Fassung zum PDF. */
    bildZuPdf?: boolean;
    /** Grad im Uhrzeigersinn. */
    drehung?: number;
    /** Seiten umsortiert, gelöscht oder eingefügt. */
    seitenGeaendert?: boolean;
    entfernt?: number;
    /** Geschwärzte Stellen (T-363). */
    geschwaerzt?: number;
    /** Durchgestrichene Textstellen (T-363). */
    durchgestrichen?: number;
    zeilen?: number;
    verschoben?: number;
    /** PDF.js-`annotationType` je neuer oder geänderter Anmerkung. */
    anmerkungen?: readonly number[];
    /** Anmerkungen, die aus der Datei gelöscht wurden. */
    anmerkungenGeloescht?: number;
    /** Ausgefüllte Formularfelder. */
    formularfelder?: number;
    /** Der Editor meldet Änderungen, ohne dass sie sich zählen ließen. */
    sonstGeaendert?: boolean;
}

const ANMERKUNG: Record<number, [string, string]> = {
    3: ["Text eingefügt", "Texte eingefügt"],
    9: ["Markierung gesetzt", "Markierungen gesetzt"],
    13: ["Bild, Stempel oder Unterschrift eingefügt", "Bilder, Stempel oder Unterschriften eingefügt"],
    15: ["Zeichnung eingefügt", "Zeichnungen eingefügt"],
    101: ["Unterschrift eingefügt", "Unterschriften eingefügt"],
};

const zahl = (n: number, eins: string, viele: string): string => `${n} ${n === 1 ? eins : viele}`;

export function aenderungsText(a: AenderungsAngaben): string {
    const teile: string[] = [];
    if (a.ausFassung) teile.push(`aus Fassung ${a.ausFassung} weiterbearbeitet`);
    if (a.bildZuPdf) teile.push("Bild in PDF umgewandelt");
    if (a.drehung) teile.push(`um ${a.drehung}° gedreht`);
    if (a.seitenGeaendert) teile.push("Seiten umsortiert, gelöscht oder eingefügt");
    if (a.entfernt) teile.push(zahl(a.entfernt, "Bereich entfernt", "Bereiche entfernt"));
    if (a.geschwaerzt) teile.push(zahl(a.geschwaerzt, "Stelle geschwärzt", "Stellen geschwärzt"));
    if (a.durchgestrichen) teile.push(zahl(a.durchgestrichen, "Stelle durchgestrichen", "Stellen durchgestrichen"));
    if (a.zeilen) teile.push(zahl(a.zeilen, "Textzeile geändert", "Textzeilen geändert"));
    if (a.verschoben) teile.push(zahl(a.verschoben, "Ausschnitt verschoben", "Ausschnitte verschoben"));
    const jeArt = new Map<number, number>();
    let unbekannt = 0;
    for (const t of a.anmerkungen ?? []) {
        if (ANMERKUNG[t]) jeArt.set(t, (jeArt.get(t) ?? 0) + 1);
        else unbekannt += 1;
    }
    for (const [t, n] of jeArt) teile.push(zahl(n, ...ANMERKUNG[t]!));
    if (unbekannt) teile.push(zahl(unbekannt, "Anmerkung eingefügt", "Anmerkungen eingefügt"));
    if (a.anmerkungenGeloescht) teile.push(zahl(a.anmerkungenGeloescht, "Anmerkung gelöscht", "Anmerkungen gelöscht"));
    if (a.formularfelder) teile.push(zahl(a.formularfelder, "Formularfeld ausgefüllt", "Formularfelder ausgefüllt"));
    const gezaehlt = jeArt.size > 0 || unbekannt > 0 || !!a.anmerkungenGeloescht || !!a.formularfelder;
    if (a.sonstGeaendert && !gezaehlt) teile.push("Inhalte eingefügt oder geändert");
    return teile.join(" · ").slice(0, 500);
}

/**
 * Was im Anmerkungs-Speicher von PDF.js liegt, nach Art gezählt. Der Speicher
 * ist eine interne Struktur — jede Überraschung endet hier still bei „nichts
 * gezählt“, dann steht in der Historie der allgemeine Satz.
 */
export function zaehleAnmerkungen(speicher: unknown): Pick<AenderungsAngaben, "anmerkungen" | "anmerkungenGeloescht" | "formularfelder"> {
    const anmerkungen: number[] = [];
    let anmerkungenGeloescht = 0;
    let formularfelder = 0;
    try {
        const map = (speicher as { serializable?: { map?: Map<string, unknown> | null } } | null)?.serializable?.map;
        if (map && typeof map.forEach === "function") {
            map.forEach((wert) => {
                const w = wert as { annotationType?: unknown; deleted?: unknown; value?: unknown } | null;
                if (!w || typeof w !== "object") return;
                if (w.deleted === true) anmerkungenGeloescht += 1;
                else if (typeof w.annotationType === "number") anmerkungen.push(w.annotationType);
                else if ("value" in w) formularfelder += 1;
            });
        }
    } catch {
        // siehe oben
    }
    return { anmerkungen, anmerkungenGeloescht, formularfelder };
}
